import { scheduleInput } from '@trafiku/contracts';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ResourceList } from '@/components/resource-list';
import { Combobox } from '@/components/ui/combobox';
import { Field, Input, TagInput } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { safeHex, textOn } from '@/lib/colors';
import { useFormState } from '@/lib/form';
import { timeAgo } from '@/lib/format';
import { t, tn } from '@/lib/i18n';
import { lineOptions, useNetwork } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import { useLineTimetables } from '@/lib/timetables';
import type { Schedule } from '@/lib/types';

const isTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
const normaliseTime = (v: string) => {
  const m = v.match(/^(\d{1,2})[:.]?(\d{2})$/);
  return m ? `${m[1]!.padStart(2, '0')}:${m[2]}` : v;
};

/** Timetables: by line (the usual way to edit them) or one station of a line at a time. */
export function SchedulesPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'stations' ? 'stations' : 'lines';
  const tabs = (
    <div className="mb-4 flex gap-1 border-b border-slate-200">
      {(['lines', 'stations'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => setParams(v === 'lines' ? {} : { view: v })}
          className={`-mb-px border-b-2 px-3 py-2 text-sm ${
            view === v
              ? 'border-brand-600 font-medium text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          {v === 'lines' ? t('By direction') : t('By station')}
        </button>
      ))}
    </div>
  );
  return view === 'lines' ? <LineTimetables tabs={tabs} /> : <StationTimetables tabs={tabs} />;
}

function LineTimetables({ tabs }: { tabs: ReactNode }) {
  const lines = useLineTimetables();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const items = (lines.data?.items ?? []).filter((l) =>
    l.name.toLowerCase().includes(q.trim().toLowerCase()),
  );
  // Going and return line of a zone next to each other.
  items.sort(
    (a, b) =>
      (a.zone?.name ?? '~').localeCompare(b.zone?.name ?? '~') ||
      (a.zone?.direction ?? '').localeCompare(b.zone?.direction ?? '') ||
      a.name.localeCompare(b.name),
  );
  const span = (d: { count: number; first: string | null; last: string | null }) =>
    d.count ? (
      <span>
        {tn(d.count, '{n} departure', '{n} departures')}{' '}
        <span className="text-slate-400">
          {d.first}–{d.last}
        </span>
      </span>
    ) : (
      <span className="text-slate-400">—</span>
    );
  return (
    <>
      <PageHeader
        title={t('Timetables')}
        subtitle={t('Departure times per direction and station, shown in the app.')}
      />
      {tabs}
      <Card padded={false}>
        <div className="border-b border-slate-200 p-3">
          <Input
            className="w-72"
            placeholder={t('Search…')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('Search')}
          />
        </div>
        {lines.isLoading ? (
          <Spinner />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2.5">{t('Direction')}</th>
                <th className="px-4 py-2.5">{t('Stations with times')}</th>
                <th className="px-4 py-2.5">{t('Weekdays')}</th>
                <th className="px-4 py-2.5">{t('Weekend')}</th>
                <th className="px-4 py-2.5">{t('Last edited')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((l) => {
                const color = safeHex(l.zone?.color, '#94a3b8');
                const complete = l.stations > 0 && l.with_times === l.stations;
                return (
                  <tr
                    key={l._id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/schedules/directions/${l._id}`)}
                  >
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold"
                          style={{ background: color, color: textOn(color) }}
                        >
                          {l.zone?.bus_sign ?? '–'}
                        </span>
                        <span className="font-medium">{l.name}</span>
                        {l.zone && (
                          <span className="text-xs text-slate-400">
                            {l.zone.direction === 'going' ? t('going') : t('return')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={
                          complete ? 'text-emerald-700' : l.with_times ? 'text-amber-700' : 'text-slate-400'
                        }
                      >
                        {l.with_times} / {l.stations}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">{span(l.week)}</td>
                    <td className="px-4 py-2.5">{span(l.weekend)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {l.plan
                        ? `${timeAgo(l.plan.updated_at)}${l.plan.updated_by ? ` · ${l.plan.updated_by}` : ''}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function StationTimetables({ tabs }: { tabs: ReactNode }) {
  const network = useNetwork();
  return (
    <ResourceList<Schedule>
      title={t('Timetables')}
      subtitle={t('Departure times per direction and station, shown in the app.')}
      toolbar={tabs}
      resource="schedules"
      basePath="/schedules"
      newLabel={t('New timetable')}
      filters={(set, v) => (
        <Combobox
          className="w-64"
          value={(v.line as string) ?? ''}
          onChange={(id) => set('line', id)}
          emptyLabel={t('All directions')}
          placeholder={t('All directions')}
          options={lineOptions(network.data?.lines)}
        />
      )}
      columns={[
        { header: t('Direction'), cell: (s) => <span className="font-medium">{s.line?.name}</span> },
        { header: t('Station'), cell: (s) => s.station?.name },
        { header: t('Weekdays'), cell: (s) => tn(s.week.length, '{n} departure', '{n} departures') },
        { header: t('Weekend'), cell: (s) => tn(s.weekend.length, '{n} departure', '{n} departures') },
        { header: t('First'), cell: (s) => s.week[0] ?? s.weekend[0] ?? '—' },
      ]}
    />
  );
}

function TimeList({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  return (
    <TagInput
      values={values}
      onChange={(v) => onChange([...new Set(v.map(normaliseTime))].filter(isTime).sort())}
      validate={(v) => isTime(normaliseTime(v))}
      placeholder={t('06:00 06:20 … (Enter, comma or paste)')}
    />
  );
}

export function ScheduleEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Schedule>('schedules', id);
  const network = useNetwork();
  const save = useSave<Schedule>('schedules', id);
  const navigate = useNavigate();
  const form = useFormState({ line: '', station: '', week: [] as string[], weekend: [] as string[] });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const s = item.data;
    if (s) form.setValues({ line: s.line._id, station: s.station._id, week: s.week, weekend: s.weekend });
  }, [item.data]);
  const { values, set, errors } = form;
  const line = network.data?.lines.find((l) => l._id === values.line);
  const stations = useMemo(
    () =>
      line
        ? line.ordered_stations
            .map((sid) => network.data!.stations.find((s) => s._id === sid))
            .filter(Boolean)
        : [],
    [line, network.data],
  );
  if ((!isNew && item.isLoading) || !network.data) return <Spinner />;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(scheduleInput, (d) => save.mutateAsync(d)))
          navigate('/schedules?view=stations');
      }}
    >
      <PageHeader title={isNew ? t('New timetable') : t('Timetable')} back="/schedules?view=stations" />
      {line && (
        <p className="mb-4 rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-900 ring-1 ring-sky-200">
          {t('This changes one station only. To set the times of every station of the direction at once:')}{' '}
          <Link to={`/schedules/directions/${line._id}`} className="font-semibold underline">
            {t('Timetable of {line}', { line: line.name })}
          </Link>
        </p>
      )}
      <Card>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Direction')} error={errors.line}>
              {(fid) => (
                <Combobox
                  id={fid}
                  value={values.line}
                  onChange={(id) => set('line', id)}
                  invalid={!!errors.line}
                  placeholder={t('Search direction…')}
                  options={lineOptions(network.data?.lines)}
                />
              )}
            </Field>
            <Field label={t('Station')} error={errors.station}>
              {(fid) => (
                <Combobox
                  id={fid}
                  value={values.station}
                  onChange={(id) => set('station', id)}
                  disabled={!line}
                  invalid={!!errors.station}
                  placeholder={line ? t('Search station…') : t('Choose a direction first')}
                  options={stations.map((s, i) => ({ value: s!._id, label: `${i + 1}. ${s!.name}` }))}
                />
              )}
            </Field>
          </div>
          <Field label={t('Weekdays ({n})', { n: values.week.length })} error={errors.week}>
            {() => <TimeList values={values.week} onChange={(v) => set('week', v)} />}
          </Field>
          <Field label={t('Weekend ({n})', { n: values.weekend.length })} error={errors.weekend}>
            {() => <TimeList values={values.weekend} onChange={(v) => set('weekend', v)} />}
          </Field>
        </div>
        <div className="mt-6">
          <FormActions
            saving={save.isPending}
            resource="schedules"
            id={id}
            backTo="/schedules?view=stations"
          />
        </div>
      </Card>
    </form>
  );
}
